import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthHeader, defaultAuthMenuItems } from '../auth/AuthHeader';
import { apiClient } from '../../api/httpClient';
import { zynity_api } from '../../config/external-routes';
import { getApiMessage } from './registrationUtils';
import '../auth/LoginPage.css';
import './PeopleRegistration.css';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

const RECAPTCHA_ACTION = 'help_request';

const loadRecaptchaV3 = (siteKey: string) => {
  if (typeof window === 'undefined') return Promise.reject();
  if (window.grecaptcha && window.grecaptcha.ready) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById('recaptcha-script-v3') as HTMLScriptElement | null;
  if (existingScript) {
    return new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'recaptcha-script-v3';
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.body.appendChild(script);
  });
};

const executeRecaptchaV3 = async (siteKey: string): Promise<string> => {
  if (!window.grecaptcha || !window.grecaptcha.ready) {
    throw new Error('recaptcha-not-ready');
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(siteKey, { action: RECAPTCHA_ACTION })
        .then((token: string) => {
          if (token) {
            resolve(token);
          } else {
            reject();
          }
        })
        .catch(() => reject());
    });
  });
};

export const PeopleRegisterPage: React.FC = () => {
  const location = useLocation();
  const [formValues, setFormValues] = useState({
    FirstName: '',
    LastName: '',
    Phone: '',
    EmailAddress: '',
    OrganizationName: '',
  });
  const [statusMessage, setStatusMessage] = useState('');
  const [statusTone, setStatusTone] = useState<'success' | 'error' | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const showCoachCallout = queryParams.get('act') === 'cc';
  const showEet = queryParams.get('eet') === 'yes';
  const rootUrl = process.env.REACT_APP_ZYNITY_YII_URL || '';
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const handleChange =
    (field: keyof typeof formValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    setStatusMessage('');
    setStatusTone('');

    setIsSubmitting(true);
    let captchaToken = '';
    try {
      if (!recaptchaSiteKey) {
        throw new Error('missing-site-key');
      }
      await loadRecaptchaV3(recaptchaSiteKey);
      captchaToken = await executeRecaptchaV3(recaptchaSiteKey);

      const payload: any = {
        FirstName: formValues.FirstName.trim(),
        LastName: formValues.LastName.trim(),
        Phone: formValues.Phone.trim(),
        EmailAddress: formValues.EmailAddress.trim(),
        OrganizationName: formValues.OrganizationName.trim(),
        captchaToken,
        recaptchaToken: captchaToken,
        gRecaptchaResponse: captchaToken,
        'g-recaptcha-response': captchaToken,
      };
      if (showEet) {
        payload.eet = 'yes';
      }

      const response = await apiClient.post(zynity_api.people_register, payload);
      const message = getApiMessage(response.data) || 'Registration submitted successfully.';
      setStatusMessage(message);
      setStatusTone('success');
      setFormValues({
        FirstName: '',
        LastName: '',
        Phone: '',
        EmailAddress: '',
        OrganizationName: '',
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to submit registration. Please try again.';
      setStatusMessage(message);
      setStatusTone('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-page register-page">
      <AuthHeader menuItems={defaultAuthMenuItems} />

      <main className="registration-content">
        <h1 className="registration-title">New Member Registration</h1>
        <div className="registration-card">
          {statusMessage && (
            <p className={`registration-message ${statusTone}`}>{statusMessage}</p>
          )}

          <div className="registration-intro">
            <p>Complete and submit the form below to create your Zynity™ executive account.</p>
            <p>You will receive an email with a link to finalize your registration.</p>
            <p>
              For assistance, you can contact us using the Help Request form.{' '}
              <a href={`${rootUrl}/site/helpRequest`}>Help Request</a>
            </p>
          </div>

          {showCoachCallout && (
            <div className="registration-callout">
              Thank you for your interest to become a Certified Coach. The first step is to
              complete and submit this form to create your Zynity member account. Then, after you
              log in you will be able to complete your Certified Coach application.
            </div>
          )}

          {showEet && (
            <p className="registration-intro">
              Complete the registration form below and submit. You will be presented a link to
              download a PDF copy of the Exceptional Executive Teams book.
            </p>
          )}

          <p className="registration-note">Fields marked with * are required.</p>

          <form className="registration-form" onSubmit={handleSubmit}>
            <div className="registration-grid">
              <div className="registration-field">
                <label className="sr-only" htmlFor="FirstName">First Name *</label>
                <input
                  id="FirstName"
                  name="FirstName"
                  placeholder="First Name *"
                  value={formValues.FirstName}
                  onChange={handleChange('FirstName')}
                  required
                />
              </div>
              <div className="registration-field">
                <label className="sr-only" htmlFor="LastName">Last Name *</label>
                <input
                  id="LastName"
                  name="LastName"
                  placeholder="Last Name *"
                  value={formValues.LastName}
                  onChange={handleChange('LastName')}
                  required
                />
              </div>
              <div className="registration-field">
                <label className="sr-only" htmlFor="Phone">Phone</label>
                <input
                  id="Phone"
                  name="Phone"
                  placeholder="Phone"
                  value={formValues.Phone}
                  onChange={handleChange('Phone')}
                />
              </div>
              <div className="registration-field">
                <label className="sr-only" htmlFor="EmailAddress">Email *</label>
                <input
                  id="EmailAddress"
                  name="EmailAddress"
                  type="email"
                  placeholder="Email *"
                  value={formValues.EmailAddress}
                  onChange={handleChange('EmailAddress')}
                  required
                />
                <span className="registration-helper align-right">Also Username for login.</span>
              </div>
              <div className="registration-field honeypot">
                <label className="sr-only" htmlFor="OrganizationName">Organization Name</label>
                <input
                  id="OrganizationName"
                  name="OrganizationName"
                  value={formValues.OrganizationName}
                  onChange={handleChange('OrganizationName')}
                />
              </div>
              {showEet && (
                <input type="hidden" name="eet" value="yes" />
              )}
            </div>
            <button type="submit" className="registration-submit" disabled={isSubmitting}>
              Submit
            </button>
          </form>
        </div>
        <p className="registration-footer">Copyright © 2015-2026 Zynity. All rights reserved.</p>
      </main>
    </div>
  );
};
